import { useId, useLayoutEffect, type RefObject } from "react";
import type { ConnectionAnalyser } from "./ConnectionAnalyser";
import type { ConnectionEndpointProvider } from "./ConnectionEndpointProvider";
import { navigatorConnectionEndpoints } from "./navigatorConnectionEndpoints";
import { visibleControlPoint } from "./controlAnchorPoint";
import { waitForNavigatorRow } from "./waitForNavigatorRow";
import { DEFAULT_NAVIGATOR_ANCHOR_OPTIONS, type NavigatorAnchorOptions } from "./navigatorAnchorOptions";
import { collectConnectableControls } from "./connectableControlEndpoint";
import { navigatorEndpointGeometry } from "./endpointContour";

/** Lets the navigator resolve device IDs inside its own DOM, with visible ancestor fallbacks. */
export function useNavigatorConnectionEndpoints(
  analyser: ConnectionAnalyser,
  root: RefObject<HTMLElement | null>,
  rowId: (source: "navigator" | "commander", id: string) => string,
  options: NavigatorAnchorOptions = DEFAULT_NAVIGATOR_ANCHOR_OPTIONS,
  revealObject?: (objectId: string) => Promise<void>,
): void {
  const id = useId();
  useLayoutEffect(() => {
    const provider: ConnectionEndpointProvider = {
      id: `navigator:${id}`,
      getConnectableEndpoints() {
        return collectConnectableControls(root.current, "navigator", (objectId) => state.objectRegistry.getObject(objectId));
      },
      async bringIntoView(objectId, targetObjectId) {
        const targetId = targetObjectId ?? objectId;
        if (revealObject) await revealObject(targetId);
        if (!root.current?.isConnected) return;
        const navigatorRoot = root.current;
        const targetSelector = `#${CSS.escape(rowId("navigator", targetId))}`;
        const target = revealObject
          ? await waitForNavigatorRow(navigatorRoot, targetSelector)
          : navigatorRoot.querySelector(targetSelector);
        if (!navigatorRoot.isConnected || root.current !== navigatorRoot) return;
        const element = target ?? navigatorRoot.querySelector(`#${CSS.escape(rowId("navigator", objectId))}`);
        if (!element) return;
        const point = visibleControlPoint(element);
        const actual = visibleControlPoint(element, { scrollBehavior: "follow" });
        if (!actual) return;
        if (!point || Math.abs(point.clientY - actual.clientY) > 0.5 || Math.abs(point.clientX - actual.clientX) > 0.5) {
          element.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });
        }
      },
      getConnectionEndpoint(connection) {
        const endpoints = navigatorConnectionEndpoints(
          connection,
          (objectId) => {
            const element = root.current?.querySelector(`#${CSS.escape(rowId("navigator", objectId))}`);
            return element ? visibleControlPoint(element, options) : undefined;
          },
          options,
        );
        for (const endpoint of endpoints) {
          if (!endpoint.anchorObjectId) continue;
          const element = root.current?.querySelector(`#${CSS.escape(rowId("navigator", endpoint.anchorObjectId))}`);
          if (!element) continue;
          const geometry = navigatorEndpointGeometry(element, endpoint.point);
          endpoint.point = geometry.point;
          endpoint.contour = geometry.contour;
        }
        return endpoints;
      },
    };
    analyser.registerEndpointProvider(provider);
    const resize = new ResizeObserver(() => analyser.refreshEndpoints());
    if (root.current) resize.observe(root.current);
    return () => {
      resize.disconnect();
      analyser.unregisterEndpointProvider(provider);
    };
  }, [analyser, root, rowId, options, revealObject, id]);
}
